/**
 * Service for ModoForja global settings
 */
import { supabase } from '@/integrations/supabase/client';

export type AudioSource = 'local' | 'radio';

export interface ModoForjaSettings {
  default_audio_source: AudioSource;
  default_track_id: string | null;
  default_station_id: string | null;
}

/**
 * Fetch all ModoForja settings
 */
export async function fetchModoForjaSettings(): Promise<ModoForjaSettings> {
  const { data, error } = await supabase
    .from('modoforja_settings')
    .select('key, value');

  if (error || !data) {
    console.error('Error fetching modoforja settings:', error);
    return {
      default_audio_source: 'local',
      default_track_id: null,
      default_station_id: null,
    };
  }

  const settings: Record<string, string | null> = {};
  data.forEach(row => {
    settings[row.key] = row.value;
  });

  return {
    default_audio_source: (settings.default_audio_source as AudioSource) || 'local',
    default_track_id: settings.default_track_id || null,
    default_station_id: settings.default_station_id || null,
  };
}

/**
 * Update a single ModoForja setting
 */
export async function updateModoForjaSetting(
  key: string,
  value: string | null
): Promise<boolean> {
  const { error } = await supabase
    .from('modoforja_settings')
    .update({ 
      value, 
      updated_at: new Date().toISOString(),
      updated_by: (await supabase.auth.getUser()).data.user?.id 
    })
    .eq('key', key);

  if (error) {
    console.error('Error updating modoforja setting:', error);
    return false;
  }

  return true;
}

/**
 * Update multiple ModoForja settings at once
 */
export async function updateModoForjaSettings(
  settings: Partial<ModoForjaSettings>
): Promise<boolean> {
  const userId = (await supabase.auth.getUser()).data.user?.id;
  const updates: Promise<boolean>[] = [];

  if (settings.default_audio_source !== undefined) {
    const promise = supabase
      .from('modoforja_settings')
      .update({ value: settings.default_audio_source, updated_at: new Date().toISOString(), updated_by: userId })
      .eq('key', 'default_audio_source')
      .then(({ error }) => !error);
    updates.push(Promise.resolve(promise));
  }

  if (settings.default_track_id !== undefined) {
    const promise = supabase
      .from('modoforja_settings')
      .update({ value: settings.default_track_id, updated_at: new Date().toISOString(), updated_by: userId })
      .eq('key', 'default_track_id')
      .then(({ error }) => !error);
    updates.push(Promise.resolve(promise));
  }

  if (settings.default_station_id !== undefined) {
    const promise = supabase
      .from('modoforja_settings')
      .update({ value: settings.default_station_id, updated_at: new Date().toISOString(), updated_by: userId })
      .eq('key', 'default_station_id')
      .then(({ error }) => !error);
    updates.push(Promise.resolve(promise));
  }

  const results = await Promise.all(updates);
  return results.every(Boolean);
}
